import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, ArrowLeft, MessageSquareWarning } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '@/lib/api';

/**
 * Página pública que renderiza dinámicamente un documento legal desde la
 * colección `legal_documents`. El contenido, el título y la versión se
 * cargan por API — el admin puede editarlos desde `/admin/documentos-legales`
 * sin necesidad de tocar código ni redeploy.
 *
 * Rutas que la usan:
 *   /terminos        -> slug `terms`
 *   /politicas       -> slug `purchase`
 *   /devoluciones    -> slug `returns`
 *   /privacidad      -> slug `privacy`
 */
const SLUG_META = {
  terms: {
    label: 'Términos y Condiciones',
    fallbackTitle: 'Términos y Condiciones',
    kicker: 'Marco legal',
  },
  purchase: {
    label: 'Política de Compras',
    fallbackTitle: 'Política de Compras',
    kicker: 'Reglas de compra',
  },
  returns: {
    label: 'Política de Devoluciones',
    fallbackTitle: 'Política de Devoluciones, Cambios y Reembolsos',
    kicker: 'Cambios y reembolsos',
  },
  privacy: {
    label: 'Política de Privacidad',
    fallbackTitle: 'Política de Tratamiento de Datos Personales',
    kicker: 'Datos personales',
  },
  shipping: {
    label: 'Política de Tarifas, Envíos y Subsidios',
    fallbackTitle: 'Política de Tarifas, Envíos y Subsidios',
    kicker: 'Envíos y tarifas',
  },
};

export default function LegalDocument({ slug, canonical }) {
  const meta = SLUG_META[slug] || { fallbackTitle: 'Documento legal', kicker: 'Legal' };
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let ok = true;
    setDoc(null);
    setError('');
    api
      .get(`/legal/${slug}`)
      .then(({ data }) => { if (ok) setDoc(data); })
      .catch((e) => { if (ok) setError(e.response?.data?.detail || 'No se pudo cargar el documento.'); });
    return () => { ok = false; };
  }, [slug]);

  useSEO({
    title: `${(doc && doc.title) || meta.fallbackTitle} | AutoMarket Colombia`,
    description: `${meta.label} vigente de AutoMarket Colombia.`,
    canonical,
  });

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-orange-500 text-slate-900">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">{meta.kicker}</div>
            <h1
              className="font-display text-3xl font-black tracking-tighter"
              data-testid={`legal-${slug}-title`}
            >
              {(doc && doc.title) || meta.fallbackTitle}
            </h1>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        {doc && (
          <div
            className="flex flex-wrap items-center gap-3 text-xs text-slate-600 mb-6"
            data-testid={`legal-${slug}-meta`}
          >
            <span className="inline-flex items-center gap-1 border border-slate-300 bg-slate-50 px-2 py-1">
              <strong className="font-semibold text-slate-900">Versión:</strong>{' '}
              <span data-testid={`legal-${slug}-version`}>{String(doc.version || '1.0').replace(/-provisional$/i, '')}</span>
            </span>
            {doc.effective_at && (
              <span className="inline-flex items-center gap-1 border border-slate-300 bg-slate-50 px-2 py-1">
                <strong className="font-semibold text-slate-900">Vigente desde:</strong>{' '}
                {new Date(doc.effective_at).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="border border-red-300 bg-red-50 text-red-900 px-4 py-3 text-sm">{error}</div>
        )}

        {!doc && !error && (
          <div className="text-sm text-slate-500" data-testid={`legal-${slug}-loading`}>
            Cargando documento…
          </div>
        )}

        {doc && (
          <article
            className="legal-doc-body max-w-none text-slate-700 leading-relaxed
              [&_h1]:font-display [&_h1]:font-black [&_h1]:tracking-tight [&_h1]:text-3xl [&_h1]:text-slate-900 [&_h1]:mt-8 [&_h1]:mb-4
              [&_h2]:font-display [&_h2]:font-black [&_h2]:tracking-tight [&_h2]:text-2xl [&_h2]:text-slate-900 [&_h2]:mt-8 [&_h2]:mb-3
              [&_h3]:font-display [&_h3]:font-bold [&_h3]:text-lg [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2
              [&_p]:my-3 [&_p]:text-sm
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul>li]:text-sm [&_ul>li]:my-1
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_ol>li]:text-sm [&_ol>li]:my-1
              [&_strong]:text-slate-900 [&_strong]:font-semibold
              [&_a]:text-blue-700 hover:[&_a]:underline
              [&_hr]:my-8 [&_hr]:border-slate-200"
            data-testid={`legal-${slug}-body`}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body}</ReactMarkdown>
          </article>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-3 text-sm border-t border-slate-200 pt-6">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-700 hover:underline font-semibold">
            <ArrowLeft className="h-4 w-4" /> Ir al inicio
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/pqr" className="inline-flex items-center gap-2 text-blue-700 hover:underline font-semibold">
            <MessageSquareWarning className="h-4 w-4" /> Radicar PQR
          </Link>
        </div>
      </div>
    </Layout>
  );
}
